import * as React from 'react';
import { cn } from '../../lib/utils';
import type { Column } from '../../shared/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import { ErrorDisplay } from './error-display';
import { EmptyState } from './empty-state';
import { TableSkeleton } from './table-skeleton';



export interface DataTableProps<T> {
  columns: Column<T>[];
  data?: T[];
  loading?: boolean;
  error?: Error | string;
  onRetry?: () => void;
  emptyState?: {
    title?: string;
    description?: string;
    icon?: React.ReactNode;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
  rowKey?: keyof T | ((record: T) => string);
  onRowClick?: (record: T, index: number) => void;
  className?: string;
  tableClassName?: string;
}

function DataTable<T extends Record<string, unknown>>({
  columns,
  data = [],
  loading = false,
  error,
  onRetry,
  emptyState,
  rowKey = 'id' as keyof T,
  onRowClick,
  className,
  tableClassName,
}: DataTableProps<T>) {
  const getRowKey = React.useCallback(
    (record: T, index: number): string => {
      if (typeof rowKey === 'function') {
        return rowKey(record);
      }
      return String(record[rowKey] ?? index);
    },
    [rowKey]
  );

  const getValue = React.useCallback((record: T, key: string): T[keyof T] => {
    const value = key.split('.').reduce((obj: unknown, k: string) => {
      return obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[k] : undefined;
    }, record);
    return value as T[keyof T];
  }, []);

  if (loading) {
    return (
      <div className={cn('w-full', className)}>
        <TableSkeleton
          rows={5}
          columns={columns.length}
          showHeader={true}
          className="rounded-md border"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('w-full', className)}>
        <ErrorDisplay
          title="Failed to load data"
          error={error}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('w-full', className)}>
        <div className="rounded-md border">
          <Table className={tableClassName}>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    style={{ width: column.width }}
                    className={cn(
                      column.align === 'center' && 'text-center',
                      column.align === 'right' && 'text-right'
                    )}
                  >
                    {column.title}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
          </Table>
        </div>
        <EmptyState
          title={emptyState?.title}
          description={emptyState?.description}
          icon={emptyState?.icon}
          action={emptyState?.action}
        />
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <div className="rounded-md border">
        <Table className={tableClassName}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width }}
                  className={cn(
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right'
                  )}
                >
                  {column.title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((record, index) => (
              <TableRow
                key={getRowKey(record, index)}
                className={cn(onRowClick && 'cursor-pointer hover:bg-muted/50')}
                onClick={() => onRowClick?.(record, index)}
              >
                {columns.map((column) => {
                  const value = getValue(record, column.key);
                  const content = column.render
                    ? column.render(value, record, index)
                    : value;

                  return (
                    <TableCell
                      key={column.key}
                      className={cn(
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right'
                      )}
                    >
                      {content as React.ReactNode}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export { DataTable };
